import type { FieldEntityTarget } from '@gat-ship/types'
import type { SystemFieldDefinition } from './types'

/**
 * Module-level singleton registries for entity targets and their system fields.
 *
 * ## How it works
 *
 * This uses a **side-effect registration pattern**: each feature that owns
 * an entity type calls {@link registerTarget} and {@link defineSystemFields}
 * at module load time (via a bare `import './custom-field-definitions'` in
 * its `index.ts`). Those calls populate the registries before any consumer
 * reads them.
 *
 * The custom-fields admin UI ({@link getRegisteredTargets}, {@link getSystemFields})
 * reads from the same Map instances — ES modules are singletons, so writers
 * and readers share one Map without importing each other.
 *
 * ## Adding a new entity target
 *
 * 1. Create a `custom-field-definitions.ts` in your feature (see
 *    `features/stakeholders/src/custom-field-definitions.ts` for an example).
 * 2. Call `registerTarget('YourEntity.Subtype', { label: 'Display Name' })`.
 * 3. Call `defineSystemFields('YourEntity.Subtype', [...])` with the fields.
 * 4. Add `import './custom-field-definitions'` to your feature's `index.ts`.
 *
 * The new target will automatically appear in the admin UI dropdown.
 */

// ── Target metadata registry ────────────────────────────────────────

export interface TargetMetadata {
  label: string
  group: string
}

const targetRegistry = new Map<FieldEntityTarget, TargetMetadata>()

/** Registers display metadata for an entity target. */
export function registerTarget(
  target: FieldEntityTarget,
  metadata: TargetMetadata
) {
  targetRegistry.set(target, metadata)
}

/** Returns all registered entity targets with their metadata. */
export function getRegisteredTargets(): {
  target: FieldEntityTarget
  label: string
  group: string
}[] {
  return [...targetRegistry.entries()].map(([target, meta]) => ({
    target,
    label: meta.label,
    group: meta.group
  }))
}

// ── System field registry ───────────────────────────────────────────

const systemFieldRegistry = new Map<
  FieldEntityTarget,
  SystemFieldDefinition[]
>()

/**
 * Registers the system field definitions for a given entity target.
 * Called once per target as a side-effect import in the owning feature's
 * `index.ts` — see the module-level docs above for the full pattern.
 */
export function defineSystemFields(
  target: FieldEntityTarget,
  fields: SystemFieldDefinition[]
) {
  systemFieldRegistry.set(target, fields)
}

/** Returns the registered system field definitions for a target */
export function getSystemFields(
  target: FieldEntityTarget
): SystemFieldDefinition[] {
  return systemFieldRegistry.get(target) ?? []
}
